/**
 * PotStatusBadge Atom
 * Displays pot status as a colored chip
 * Pattern: Atomic Design - Atoms, matches TableStatusChip styling
 */

import { Chip } from '@mui/material';
import { PotStatus } from '../types/pot.types';

export interface PotStatusBadgeProps {
  status: PotStatus;
}

/**
 * Get color for pot status
 * Pattern: Status color mapping following MUI theme
 */
export function getStatusColor(
  status: PotStatus
):
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'warning'
  | 'info'
  | 'success' {
  switch (status) {
    case 'Open':
      return 'info';
    case 'Verifying':
      return 'warning';
    case 'Verified':
      return 'primary';
    case 'Submitting':
      return 'warning';
    case 'Submitted':
      return 'secondary';
    case 'MissedSubmitDeadline':
      return 'error';
    case 'MissedVerificationDeadline':
      return 'warning';
    case 'Rejected':
      return 'error';
    case 'Approved':
      return 'success';
    case 'Paid':
      return 'default';
    case 'Archived':
      return 'default';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

/**
 * Get display label for pot status
 */
export function getStatusLabel(status: PotStatus): string {
  switch (status) {
    case 'MissedSubmitDeadline':
      return 'Missed Deadline';
    case 'MissedVerificationDeadline':
      return 'Missed Verification';
    default:
      return status;
  }
}

/**
 * Compact status chip optimized for table rows
 * Matches TableStatusChip styling: 22px height, 11px font, outlined variant
 */
export function PotStatusBadge({ status }: PotStatusBadgeProps) {
  return (
    <Chip
      label={getStatusLabel(status)}
      color={getStatusColor(status)}
      variant="outlined"
      size="small"
      sx={{
        height: 22,
        fontSize: '0.6875rem', // 11px
        fontWeight: 500,
        '& .MuiChip-label': {
          padding: '0 6px',
        },
      }}
    />
  );
}
